import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Users, Trash2, ArrowLeft, RefreshCw } from "lucide-react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ServiceReportsModal } from "@/components/admin/ServiceReportsModal";
import { DeleteServiceDialog } from "@/components/admin/DeleteServiceDialog";
import { PollUsersDialog } from "@/components/admin/PollUsersDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type ServiceWithReports = {
  id: string;
  title: string;
  description: string | null;
  category_id: string | null;
  price: number;
  pricing_type: string;
  delivery_method: string | null;
  service_location: {
    latitude: number;
    longitude: number;
    radius: number;
  } | null;
  created_at: string;
  user_id: string;
  service_categories?: {
    name: string;
  } | null;
  profiles: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  report_count: number;
  has_active_poll: boolean;
};

const AdminServices = () => {
  const { user, session } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [services, setServices] = useState<ServiceWithReports[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<ServiceWithReports | null>(null);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPollDialog, setShowPollDialog] = useState(false);

  const fetchServices = async () => {
    if (!user || !isAdmin) return;

    try {
      setServicesLoading(true);
      console.log("[ADMIN SERVICES] Starting to fetch all services...");

      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select(`
          *,
          service_categories:category_id (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (servicesError) {
        console.error("[ADMIN SERVICES] Error fetching services:", servicesError);
        throw servicesError;
      }

      if (!servicesData || servicesData.length === 0) {
        console.log("[ADMIN SERVICES] No services found in database");
        setServices([]);
        return;
      }

      // Fetch profiles, reports, and polls
      const userIds = [...new Set(servicesData.map(service => service.user_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) {
        console.error("[ADMIN SERVICES] Error fetching profiles:", profilesError);
      }

      const profilesMap = new Map();
      if (profilesData) {
        profilesData.forEach((profile) => {
          profilesMap.set(profile.id, profile);
        });
      }

      const { data: reportCounts, error: reportsError } = await supabase
        .from("service_reports")
        .select("service_id");

      if (reportsError) {
        console.error("[ADMIN SERVICES] Error fetching reports:", reportsError);
      }

      const { data: activePolls, error: pollsError } = await supabase
        .from("service_deletion_polls")
        .select("service_id")
        .eq("is_active", true);

      if (pollsError) {
        console.error("[ADMIN SERVICES] Error fetching polls:", pollsError);
      }

      const reportCountMap = new Map();
      reportCounts?.forEach((report) => {
        const count = reportCountMap.get(report.service_id) || 0;
        reportCountMap.set(report.service_id, count + 1);
      });

      const activePollsSet = new Set(activePolls?.map(poll => poll.service_id) || []);

      const servicesWithReports: ServiceWithReports[] = servicesData.map((service) => {
        let serviceLocation = null;
        if (service.service_location) {
          try {
            serviceLocation = typeof service.service_location === 'string' 
              ? JSON.parse(service.service_location) 
              : service.service_location;
          } catch (e) {
            console.error("[ADMIN SERVICES] Error parsing service location for service", service.id, ":", e);
          }
        }

        const userProfile = profilesMap.get(service.user_id);

        return {
          ...service,
          pricing_type: service.pricing_type as "fixed" | "hourly",
          delivery_method: service.delivery_method as "virtual" | "in-person" | "both" | null,
          service_location: serviceLocation,
          profiles: userProfile || null,
          report_count: reportCountMap.get(service.id) || 0,
          has_active_poll: activePollsSet.has(service.id)
        };
      });

      // Sort by report count (flagged services first)
      servicesWithReports.sort((a, b) => b.report_count - a.report_count);

      console.log("[ADMIN SERVICES] Final processed services count:", servicesWithReports.length);
      setServices(servicesWithReports);

    } catch (error: any) {
      console.error("[ADMIN SERVICES] Critical error in fetchServices:", error);
      toast.error(`Failed to load services: ${error?.message || 'Unknown error'}`);
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  useEffect(() => {
    if (!roleLoading && user && isAdmin) {
      fetchServices();
    }
  }, [user, isAdmin, roleLoading]);

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-lg font-medium text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const getProviderName = (profiles: ServiceWithReports["profiles"]) => {
    if (!profiles) return "Unknown Provider";
    
    const firstName = profiles.first_name || "";
    const lastName = profiles.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || "Unknown Provider";
  };

  const handleViewReports = (service: ServiceWithReports) => {
    setSelectedService(service);
    setShowReportsModal(true);
  };

  const handleDeleteService = (service: ServiceWithReports) => {
    setSelectedService(service);
    setShowDeleteDialog(true);
  };

  const handlePollUsers = (service: ServiceWithReports) => {
    setSelectedService(service);
    setShowPollDialog(true);
  };

  const handleServiceDeleted = () => {
    if (selectedService) {
      setServices(services.filter(s => s.id !== selectedService.id));
    }
  };

  const handlePollCreated = () => {
    if (selectedService) {
      setServices(services.map(s => 
        s.id === selectedService.id 
          ? { ...s, has_active_poll: true }
          : s
      ));
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 space-y-8">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
            <Button 
              variant="outline" 
              onClick={fetchServices}
              disabled={servicesLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${servicesLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <h1 className="font-poppins font-bold text-4xl mb-4 text-foreground">All Services</h1>
          <p className="font-inter text-xl text-muted-foreground">
            Complete list of all services on the platform (flagged services shown first)
          </p>
        </div>

        {servicesLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground font-inter mb-2">
              No services found.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((service) => (
              <Card key={service.id} className="border border-border h-fit">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-sm font-poppins mb-1 line-clamp-2 text-foreground">
                        {service.title}
                      </h3>
                      <p className="text-xs text-muted-foreground font-inter truncate">
                        {getProviderName(service.profiles)}
                      </p>
                      {service.description && (
                        <p className="text-xs text-muted-foreground font-inter mt-1 line-clamp-2">
                          {service.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {service.report_count > 0 ? (
                        <Badge variant="destructive" className="text-xs">
                          {service.report_count} report{service.report_count !== 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">No reports</Badge>
                      )}
                      {service.has_active_poll && (
                        <Badge variant="outline" className="text-xs">
                          Poll Active
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-xs text-muted-foreground font-inter space-y-1">
                      <div className="truncate">
                        {service.service_categories?.name || 'Uncategorized'}
                      </div>
                      <div>
                        ${service.price} ({service.pricing_type})
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 pt-2 border-t border-border">
                      {service.report_count > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewReports(service)}
                          className="text-xs h-7"
                        >
                          Reports
                        </Button>
                      )}
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePollUsers(service)}
                          disabled={service.has_active_poll}
                          className="text-xs h-7 flex-1"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          Poll
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteService(service)}
                          className="text-xs h-7 flex-1"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modals */}
        {selectedService && (
          <>
            <ServiceReportsModal
              service={selectedService}
              open={showReportsModal}
              onOpenChange={setShowReportsModal}
            />
            
            <DeleteServiceDialog
              service={selectedService}
              open={showDeleteDialog}
              onOpenChange={setShowDeleteDialog}
              onDeleted={handleServiceDeleted}
            />
            
            <PollUsersDialog
              service={selectedService}
              open={showPollDialog}
              onOpenChange={setShowPollDialog}
              onPollCreated={handlePollCreated}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminServices;
